import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { multipleFlags_softwareReleasesVersionDetailIssueList_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/multipleFlags_softwareReleasesVersionDetailIssueList_jiraIssue.graphql';
import type { multipleFlags_softwareReleasesVersionDetailIssueList_summarisedFlags$key } from '@atlassian/jira-relay/src/__generated__/multipleFlags_softwareReleasesVersionDetailIssueList_summarisedFlags.graphql';
import { VIEWPORT_WIDTH_MEDIUM } from '../../../../../common/constants.tsx';
import { MultipleFlagsIcon } from './icon/MultipleFlagsIcon.tsx';
import messages from './messages.tsx';
import { TooltipContent, useTooltipContentQuery } from './tooltip-content/index.tsx';

type Props = {
	jiraIssue: multipleFlags_softwareReleasesVersionDetailIssueList_jiraIssue$key;
	summarisedFlags: multipleFlags_softwareReleasesVersionDetailIssueList_summarisedFlags$key;
};

export const MultipleFlags = ({ jiraIssue, summarisedFlags }: Props): React.JSX.Element => {
	const { formatMessage, formatNumber } = useIntl();

	const issue = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment multipleFlags_softwareReleasesVersionDetailIssueList_jiraIssue on JiraIssue {
				id @required(action: THROW)
			}
		`,
		jiraIssue,
	);

	const { totalCount } = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment multipleFlags_softwareReleasesVersionDetailIssueList_summarisedFlags on DevOpsSummarisedFeatureFlags {
				totalCount @required(action: THROW)
			}
		`,
		summarisedFlags,
	);

	const [tooltipQueryReference, loadTooltipQuery] = useTooltipContentQuery(issue.id);

	const tooltipContent = (
		<Placeholder name="tooltip-content" fallback={<>{formatMessage(messages.tooltipLoading)}</>}>
			{tooltipQueryReference == null ? null : (
				<TooltipContent queryReference={tooltipQueryReference} />
			)}
		</Placeholder>
	);

	const cellContent = (
		<Container>
			<MultipleFlagsIcon />
			<TextContainer>
				{formatMessage(messages.flags, {
					count: formatNumber(totalCount),
				})}
			</TextContainer>
		</Container>
	);

	return (
		<div onFocus={loadTooltipQuery} onMouseOver={loadTooltipQuery}>
			<Tooltip
				testId="software-releases-version-detail-issue-list.ui.issues.issue-card.feature-flags.multiple-flags.tooltip"
				content={tooltipContent}
				onShow={(analyticsEvent: UIAnalyticsEvent) => {
					fireUIAnalytics(analyticsEvent, 'featureFlagSummaryTooltip');
				}}
				position="bottom-start"
			>
				{cellContent}
			</Tooltip>
		</div>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (min-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		display: 'inline',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (max-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		display: 'none',
	},
});
